import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import axios from 'axios';

import getMetaTagData from '../../../src/common/services/getMetaTagData';
import TvTopMenu from '../../../src/component/pc/tv/TopMenu';
import Box from '../../../src/component/pc/common/Box';
import Loading from '../../../src/component/pc/common/Loading';

import { getSectionDataApiUrl } from '../../../src/common/utils/tvUtil';
import { SECTION_LIST_PAGE_LIMIT } from '../../../src/common/constants/number';
import { toFit } from '../../../src/hooks/useToFit';

function Home({ defaultMetaTagData }) {
  const [sortHTxt, setSortHTxt] = useState('');
  const router = useRouter();
  const { pageSection: section, category = '', sort = '' } = router.query;

  const [pageSection, setPageSection] = useState(section);
  const [metaTagData, setMetaTagData] = useState(defaultMetaTagData);
  const [pageQueryParams, setPageQueryParams] = useState({
    section,
    category,
    sort,
    offset: 0,
    limit: SECTION_LIST_PAGE_LIMIT,
  });

  const [sectionDataList, setSectionDataList] = useState([]);
  const [sectionDataIsLoading, setSectionDataIsLoading] = useState(false);

  function sortCodeTxt(code) {
    switch (code) {
      case 'new': return '최신';
      case 'top': return '인기';
      case 'abc': return '가나다';
      case 'theme': return '추천';
      case 'all': return '전체';
      case 'N': return '방영 예정';
      case 'Y': return '방영 중';
      default: return '';
    }
  }

  function onPageQueryParamsChanged({
                                      sectionCode = '',
                                      categoryCode = '',
                                      sortCode = '',
                                    }) {
    console.log('onPageQueryParamsChanged →', { sectionCode, categoryCode, sortCode });

    setSortHTxt(sortCodeTxt(sortCode));

    const newParams = {
      section: sectionCode,
      category: categoryCode,
      sort: sortCode,
      offset: 0,
      limit: SECTION_LIST_PAGE_LIMIT,
    };

    setPageQueryParams(newParams);
    setPageSection(sectionCode);
  }

  useEffect(() => {
    window.document.addEventListener('scroll', toFit(doNextPage, {}));

    function doNextPage() {
      if (
          window.innerHeight + Math.ceil(window.pageYOffset) >=
          document.body.offsetHeight
      ) {
        setPageQueryParams((prev) => {
          const nextOffset = prev.offset + prev.limit;
          const newParams = { ...prev };
          if (sectionDataList.length === nextOffset) {
            newParams.offset = nextOffset;
            return newParams;
          }
          return prev;
        });
      }
    }
  }, [sectionDataList]);

  useEffect(() => {
    console.log('🔄 pageQueryParams changed:', pageQueryParams);

    const sectionDataApiUrl = getSectionDataApiUrl(
        pageQueryParams.offset,
        pageQueryParams.sort,
        pageQueryParams.section,
        pageQueryParams.category
    );

    const config = {
      method: 'get',
      url: sectionDataApiUrl.href,
      timeout: 3000,
    };

    setSectionDataIsLoading(true);

    axios(config)
        .then((response) => {
          const addList = response.data.map((element) => {
            const clickParamsCategory = pageQueryParams.category || 'total';
            const clickParamsSort = pageQueryParams.sort;

            return {
              type: 'poster',
              img: element.pgm_img_url,
              info: { title: element.pgm_nm },
              label: [],
              link: element.link_url,
              isNew: element.new_yn === 'Y',
              data: { pgm_nm: element.pgm_nm },
              isfree: element.free_part_yn,
              relateType: 'tv_free',
              link_new_yn: element.link_new_yn,
              clickParams: `div=tv_${clickParamsCategory}_${clickParamsSort}`,
            };
          });

          const currentOffset = Number.parseInt(sectionDataApiUrl.searchParams.get('offset')) || 0;

          if (currentOffset === 0) {
            setSectionDataList(addList);
          } else {
            setSectionDataList((prev) => [...prev, ...addList]);
          }
        })
        .catch((error) => {
          console.error('API error:', error);
        })
        .finally(() => {
          setSectionDataIsLoading(false);
        });
  }, [pageQueryParams]);

  return (
      <div className="contents">
        <Head>
          <link
              key="css_pc_url"
              rel="stylesheet"
              href={`${process.env.NEXT_PUBLIC_GLOBAL_CSS_PC_URL}?v=${process.env.NEXT_PUBLIC_BUILD_TIME}`}
          />
          <meta key="viewport" name="viewport" content="width=device-width, initial-scale=1" />
          <script
              key="gnb_js_url"
              type="text/javascript"
              src={process.env.NEXT_PUBLIC_GNB_JS_URL}
          />
          <meta name="description" content={metaTagData.shareDescription} />
          <meta name="keywords" content={metaTagData.keywords} />
          <meta name="twitter:card" content="summary" />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="ko_KR" />
          <meta property="og:site_name" content="SBS" />
          <meta property="og:title" content={metaTagData.shareTitle || ''} />
          <meta property="og:description" content={metaTagData.shareDescription || ''} />
          <meta property="og:image" content={metaTagData.shareImageUrl || ''} />
          <meta property="og:url" content={metaTagData.currentUrl || ''} />
          <meta name="nate:site_name" content="SBS" />
          <meta name="nate:title" content={metaTagData.shareTitle || ''} />
          <meta name="nate:description" content={metaTagData.shareDescription || ''} />
          <meta name="nate:image" content={metaTagData.shareImageUrl || ''} />
          <meta name="nate:url" content={metaTagData.currentUrl || ''} />
          <title>{metaTagData.shareTitle}</title>
        </Head>

        <div role="main" id="container" className="tv_listcontents_wrap">
          <TvTopMenu
              sectionCode={section}
              categoryCode={category}
              sortCode={sort}
              onChange={onPageQueryParamsChanged}
          />

          <div className="contents_area_tvlist">
            <div className="tv_list_w">
              <h3 className="hide">{sortHTxt}</h3>
              <ul className="tv_list_inner">
                {sectionDataList.map((item, index) => (
                    <li key={index} className="tv_list_cont">
                      <Box view {...item} />
                    </li>
                ))}
              </ul>
            </div>
          </div>

          {sectionDataIsLoading && <Loading />}
        </div>
      </div>
  );
}

export async function getServerSideProps(ctx) {
  const { pageSection, pageCategory } = ctx.query;
  const defaultMetaTagData = getMetaTagData(
      ctx.req.url,
      pageSection,
      pageCategory
  );

  const tvSectionTopList = await getTvSectionTopList(pageSection, pageCategory);

  if (Array.isArray(tvSectionTopList?.data) && tvSectionTopList.data.length > 0) {
    const topProgramTitleList = tvSectionTopList.data.map((item) => item.pgm_nm);
    defaultMetaTagData.keywords = defaultMetaTagData.keywords
        ? defaultMetaTagData.keywords + ',' + topProgramTitleList.join(',')
        : topProgramTitleList.join(',');
  }

  return { props: { defaultMetaTagData } };
}

async function getTvSectionTopList(sectionCode, categoryCode) {
  const sectionDataApiUrl = getSectionDataApiUrl(0, 'top', sectionCode, categoryCode, 20);
  const config = {
    method: 'get',
    url: sectionDataApiUrl.href,
    timeout: 3000,
  };

  return axios(config).catch((error) => {
    console.error('TopList API error:', error);
  });
}

export default Home;
